import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CreditCard, Loader2, PhoneCall } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PHONE_DISPLAY } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMOUNT_MIN = 1;
const AMOUNT_MAX = 10000;

const validatePayment = ({ name, phone, amount }) => {
  if (!name.trim() || !phone.trim()) return "Please enter your name and phone number.";
  if (isNaN(amount) || amount < AMOUNT_MIN || amount > AMOUNT_MAX) {
    return `Please enter an amount between $${AMOUNT_MIN} and $${AMOUNT_MAX.toLocaleString()}.`;
  }
  return null;
};

export const PayOnline = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const parsed = parseFloat(amount);

  const pay = async (e) => {
    e.preventDefault();
    const error = validatePayment({ name, phone, amount: parsed });
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
        name: name.trim(),
        phone: phone.trim(),
        amount: parsed,
      });
      window.location.href = res.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please try again or call us.");
      setLoading(false);
    }
  };

  return (
    <section id="pay" className="py-24 sm:py-32 border-b border-zinc-800/60 scroll-mt-24" data-testid="pay-online-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Pay Online</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-white mt-4 mb-6">
              Pay Your Quote Securely Online
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-lg">
              Got a quote from us over the phone or after your service? Enter the amount
              and pay securely online in seconds — no cash or checks needed.
            </p>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-center gap-3"><PhoneCall className="h-4 w-4 text-blue-500 shrink-0" /> Call {PHONE_DISPLAY} for your reliable quote first</li>
              <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-blue-500 shrink-0" /> Prices better than shops, centers &amp; dealerships</li>
              <li className="flex items-center gap-3"><CreditCard className="h-4 w-4 text-blue-500 shrink-0" /> Secure payment powered by Stripe</li>
            </ul>
          </div>

          <form onSubmit={pay} className="bg-[#121212] border border-blue-500/30 rounded-xl p-8 sm:p-10 shadow-[0_0_40px_-12px_rgba(37,99,235,0.25)]">
            <div className="flex items-baseline justify-between mb-8">
              <span className="font-heading text-lg font-semibold text-white">Service Payment</span>
              <span className="font-heading text-4xl font-bold text-blue-500" data-testid="pay-amount-display">
                {parsed >= 1 ? `$${parsed.toFixed(2)}` : "$—"}
              </span>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-zinc-300">Amount (USD)</Label>
                <Input
                  data-testid="custom-amount-input"
                  type="number"
                  min="1"
                  max="10000"
                  step="0.01"
                  className="bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600"
                  placeholder="Enter the amount quoted, e.g. 350"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Name</Label>
                <Input
                  data-testid="pay-input-name"
                  className="bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone Number</Label>
                <Input
                  data-testid="pay-input-phone"
                  type="tel"
                  className="bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600"
                  placeholder="(702) 555-0123"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <button
                data-testid="pay-submit-button"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {loading ? "Redirecting to secure checkout..." : `Pay ${parsed >= 1 ? `$${parsed.toFixed(2)}` : "Securely"}`}
              </button>
              <p className="text-zinc-600 text-xs text-center">
                You'll be redirected to Stripe's secure checkout. We'll call you to confirm.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
